import { batchStatusMap } from '@/components/BatchList';
import SridSelector from '@/components/SridSelector';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { execute } from '@/graphql/execute';
import { GetBatchQuery, ProcessJobType, UploadBatchStatus } from '@/graphql/graphql';
import { useFeature } from '@/hooks/useFeature';
import { useFeatureFlagEnabled } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { startBatchProcessing } from '@/queries/attachment.query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { ChevronDownIcon } from 'lucide-react';
import { FunctionComponent, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ProcessTypeHint from './ProcessTypeHint';
import { getProcessTypeCandidates } from './processTypeCandidates';

const getDPI = () => {
  const div = document.createElement('div');
  div.style.height = '1in';
  div.style.width = '1in';
  div.style.top = '-100%';
  div.style.left = '-100%';
  div.style.position = 'absolute';

  document.body.appendChild(div);

  const result = div.offsetHeight;

  document.body.removeChild(div);

  return result;
};

const mapboxResolution = (zoom: number, latitude = 0) => {
  const earthCircumference = 40075016.686; // meters
  const tileSize = 256; // pixels per tile
  return (earthCircumference * Math.cos((latitude * Math.PI) / 180)) / (tileSize * Math.pow(2, zoom));
};

const mapboxScale = (zoom: number, latitude = 0) => {
  const metersPerPixel = mapboxResolution(zoom, latitude);
  return (metersPerPixel * getDPI()) / 0.0254; // unitless ratio (1 : scale)
};

const niceRound = (value: number) => {
  if (value === 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / Math.pow(10, exponent);
  let niceFraction;
  if (fraction < 1.5) niceFraction = 1;
  else if (fraction < 3) niceFraction = 2;
  else if (fraction < 7) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * Math.pow(10, exponent);
};

interface iHeaderProps {
  batchId: string;
  status: UploadBatchStatus;
  srid: number;
  attachments: GetBatchQuery['batch']['attachments'];
}

const dxfOutputVersions = {
  ACAD9: 'ACAD9',
  ACAD10: 'ACAD10',
  ACAD12: 'ACAD12',
  ACAD13: 'ACAD13',
  ACAD14: 'ACAD14',
  ACAD2000: 'ACAD2000',
  ACAD2004: 'ACAD2004',
  ACAD2007: 'ACAD2007',
  ACAD2010: 'ACAD2010',
  ACAD2013: 'ACAD2013',
  ACAD2018: 'ACAD2018',
} as const;

const pointCloudAttributes = {
  rgb: 'RGB',
  intensity: 'Intensity',
  classification: 'Classification',
  'return number': 'Return Number',
  // todo: pnrext three loader does not support this yet
  // 'number of returns': 'Number of Returns',
  'point source id': 'Source ID',
} as const;

const pcAttributeKeys: Record<string, string> = {
  rgb: 'rgb',
  intensity: 'intensity',
  classification: 'classification',
  'return number': 'returnNumber',
  'point source id': 'sourceId',
};

type ProcessTypeKey = `${ProcessJobType}`;

/** Process types whose import can also queue the follow-up 2D map job. */
const MAP2D_TYPES = ['LANDXML', 'IFC_FRAGMENT'] as const;
const offersMap2d = (t: string) => (MAP2D_TYPES as readonly string[]).includes(t);

const processTypeGroups = [
  { category: 'pointCloud', types: ['E57', 'LAS', 'PTS', 'RCS'] },
  { category: 'splat', types: ['SPLAT', 'XGRIDS'] },
  { category: 'vector', types: ['GEOJSON', 'DWG', 'DXF', 'SHP', 'KML', 'GPKG'] },
  { category: 'bimAndModel', types: ['IFC_FRAGMENT', 'IFC', 'LANDXML'] },
  { category: 'ortho', types: ['GEOTIFF', 'JP2', 'JPG'] },
  {
    category: 'panoramicImage',
    types: [
      'IPRJ',
      'FJD_IMAGE',
      'TRIMBLE_IMAGE',
      'TOPCON_IMAGE',
      'SOUTH_IMAGE',
      'OMNISLAM_IMAGE',
      'LEICA_IMAGE',
      'GVI_IMAGE',
      'DJI_IMAGE',
      'CHC_IMAGE',
      'MOSAIC_IMAGE',
    ],
  },
] as const satisfies ReadonlyArray<{ category: string; types: readonly ProcessTypeKey[] }>;

/** "Local" in the SRID selector — no projection, so no georeferenced raster. */
const LOCAL_SRID = -1;

const Header: FunctionComponent<iHeaderProps> = ({ batchId, status, srid, attachments }) => {
  const { t } = useTranslation('project');
  const client = useQueryClient();
  const isPrivacyBlurEnabled = useFeatureFlagEnabled('privacyBlur');
  const rcsGate = useFeature('processing.generateRcs');
  const map2dGate = useFeature('processing.generate2dMap');
  const geotiffGate = useFeature('processing.generateGeotiff');
  const hasLasLazAttachment = attachments.some((a) => /\.(las|laz)$/i.test(a.filename));
  // GEOTIFF over LAS/LAZ rasterises the point cloud — the same PRO feature as
  // the "Generate raster GeoTIFF" add-on. A .tif upload stays free.
  const isGatedGeotiffType = (type: string) => type === 'GEOTIFF' && hasLasLazAttachment && !geotiffGate.allowed;
  const processTypeCandidates = useMemo(() => getProcessTypeCandidates(attachments), [attachments]);
  // The hint stays loud until the user has opened the select or changed its value.
  const [processTypeAcknowledged, setProcessTypeAcknowledged] = useState(false);
  const processTypeHintId = useId();

  const mutation = useMutation({
    mutationFn: async (variables: {
      srid: number;
      type: ProcessJobType;
      dxfOutputVersion?: string;
      minZoom?: number;
      maxZoom?: number;
      tileSize?: number;
      pointCloudAttributes?: string[];
      compressImages?: boolean;
      blurLicensePlates?: boolean;
      blurFaces?: boolean;
      generateRcs?: boolean;
      generate2dMap?: boolean;
      generateDecimatedPointCloud?: boolean;
      generateGeotiff?: boolean;
      rasterMode?: string;
    }) => {
      await execute(startBatchProcessing, {
        id: batchId!,
        srid: Number(variables.srid),
        type: variables.type,
        dxfOutputVersion: variables.dxfOutputVersion,
        minZoom: variables.minZoom,
        maxZoom: variables.maxZoom,
        tileSize: variables.tileSize,
        pointCloudAttributes: variables.pointCloudAttributes,
        compressImages: variables.compressImages,
        blurLicensePlates: variables.blurLicensePlates,
        blurFaces: variables.blurFaces,
        generateRcs: variables.generateRcs,
        generate2dMap: variables.generate2dMap,
        generateDecimatedPointCloud: variables.generateDecimatedPointCloud,
        generateGeotiff: variables.generateGeotiff,
        rasterMode: variables.rasterMode,
      });

      return true;
    },

    onMutate: async () => {
      await client.cancelQueries({ queryKey: ['batch', batchId] });

      const previousBatch = client.getQueryData(['batch', batchId]);

      const newBatch = JSON.parse(JSON.stringify(previousBatch));
      if (newBatch?.data?.batch) {
        newBatch.data.batch.status = 'PROCESSING';
      }

      client.setQueryData(['batch', batchId], newBatch);

      return { previousBatch, newBatch };
    },
    onError: (_, __, context) => {
      client.setQueryData(['batch', batchId], context?.previousBatch);
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: ['batch', batchId] });
    },
  });
  const formik = useFormik({
    initialValues: {
      status: status,
      srid: srid,
      processType: processTypeCandidates.types[0],
      dxfOutputVersion: dxfOutputVersions.ACAD2007,
      pointCloudAttributes: ['rgb', 'intensity'] as string[],
      compressImages: false,
      blurLicensePlates: false,
      blurFaces: false,
      generateRcs: false,
      generate2dMap: true,
      generateDecimatedPointCloud: false,
      generateGeotiff: false,
      minZoom: 12,
      maxZoom: 20,
      tileSize: 256,
      rasterMode: 'rgb',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      const geotiffOptionsActive =
        values.processType === 'GEOTIFF' ||
        values.processType === 'JP2' ||
        values.processType === 'JPG' ||
        (['LAS', 'E57', 'PTS', 'RCS'].includes(values.processType) &&
          values.generateGeotiff &&
          Number(values.srid || srid) !== LOCAL_SRID);
      if (geotiffOptionsActive) {
        if (values.minZoom < 0 || values.minZoom > 20) {
          errors.minZoom = t('batch.validationMinZoom');
        }
        if (values.maxZoom < 0 || values.maxZoom > 22) {
          errors.maxZoom = t('batch.validationMaxZoom');
        }
        if (values.minZoom >= values.maxZoom) {
          errors.minZoom = t('batch.validationMinMaxZoom');
        }
      }
      if (!values.processType) {
        errors.processType = t('batch.validationProcessType');
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (isGatedGeotiffType(values.processType)) {
        geotiffGate.promptUpgrade();
        return;
      }
      const toastId = toast.loading(t('batch.startingProcessing'));
      try {
        const isPointCloud = ['LAS', 'E57', 'PTS', 'RCS'].includes(values.processType);
        const effectiveSrid = Number(values.srid || srid);
        // Raster tiles need a real CRS; a Local (-1) point cloud can't be tiled.
        const wantsGeotiff =
          isPointCloud && geotiffGate.allowed && values.generateGeotiff && effectiveSrid !== LOCAL_SRID;
        await mutation.mutateAsync({
          srid: effectiveSrid,
          type: values.processType,
          dxfOutputVersion: values.dxfOutputVersion,
          pointCloudAttributes: ['LAS', 'E57', 'PTS', 'RCS'].includes(values.processType)
            ? values.pointCloudAttributes
            : undefined,
          minZoom: ['GEOTIFF', 'JP2', 'JPG'].includes(values.processType) || wantsGeotiff ? values.minZoom : undefined,
          maxZoom: ['GEOTIFF', 'JP2', 'JPG'].includes(values.processType) || wantsGeotiff ? values.maxZoom : undefined,
          tileSize: ['GEOTIFF', 'JP2', 'JPG'].includes(values.processType) || wantsGeotiff ? values.tileSize : undefined,
          rasterMode: values.processType === 'GEOTIFF' || wantsGeotiff ? values.rasterMode : undefined,
          compressImages: values.compressImages,
          blurLicensePlates: values.blurLicensePlates,
          blurFaces: values.blurFaces,
          generateRcs: rcsGate.allowed && values.generateRcs,
          generate2dMap: offersMap2d(values.processType) ? map2dGate.allowed && values.generate2dMap : undefined,
          generateDecimatedPointCloud: ['LAS', 'E57'].includes(values.processType)
            ? values.generateDecimatedPointCloud
            : undefined,
          generateGeotiff: isPointCloud ? wantsGeotiff : undefined,
        });
        toast.success(t('batch.processingStarted'), { id: toastId });
      } catch (e) {
        console.error(e);
        toast.error(t('batch.failedStartProcessing'), { id: toastId });
      }
    },
  });

  const isPointCloudType = ['LAS', 'E57', 'PTS', 'RCS'].includes(formik.values.processType);
  const geotiffAllowed = Number(formik.values.srid || srid) !== LOCAL_SRID;
  const generatesGeotiff = isPointCloudType && geotiffAllowed && geotiffGate.allowed && formik.values.generateGeotiff;
  const showRasterMode = (formik.values.processType === 'GEOTIFF' && hasLasLazAttachment) || generatesGeotiff;

  // Shared GeoTIFF option fields — rendered inline for the standalone GEOTIFF/JP2/JPG
  // process types, and again under the "Generate raster GeoTIFF" checkbox for
  // point-cloud uploads.
  const geotiffOptionFields = (
    <>
      <div className='flex items-start gap-1 flex-col'>
        <Label>
          {t('batch.minZoom')} ~1:{niceRound(mapboxScale(formik.values.minZoom)).toLocaleString()}
        </Label>
        <Input
          type='number'
          min={0}
          max={22}
          value={formik.values.minZoom}
          onChange={(e) => formik.setFieldValue('minZoom', parseInt(e.target.value) || 0)}
        />
        {formik.errors.minZoom && <span className='text-xs text-red-500'>{formik.errors.minZoom}</span>}
      </div>
      <div className='flex items-start gap-1 flex-col'>
        <Label>
          {t('batch.maxZoom')} ~1:{niceRound(mapboxScale(formik.values.maxZoom)).toLocaleString()}
        </Label>
        <Input
          type='number'
          min={0}
          max={22}
          value={formik.values.maxZoom}
          onChange={(e) => formik.setFieldValue('maxZoom', parseInt(e.target.value) || 0)}
        />
        {formik.errors.maxZoom && <span className='text-xs text-red-500'>{formik.errors.maxZoom}</span>}
      </div>
      <div className='flex items-start gap-1 flex-col'>
        <Label>{t('batch.tileSize')}</Label>
        <Select
          value={formik.values.tileSize.toString()}
          onValueChange={(value) => formik.setFieldValue('tileSize', parseInt(value))}
        >
          <SelectTrigger className='w-full'>
            <SelectValue placeholder={t('batch.selectTileSize')} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value='256'>256px</SelectItem>
              <SelectItem value='512'>512px</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      {showRasterMode ? (
        <div className='flex items-start gap-1 flex-col'>
          <Label>{t('batch.rasterMode')}</Label>
          <Select value={formik.values.rasterMode} onValueChange={(value) => formik.setFieldValue('rasterMode', value)}>
            <SelectTrigger className='w-full'>
              <SelectValue placeholder={t('batch.selectRasterMode')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value='rgb'>RGB</SelectItem>
                <SelectItem value='intensity'>{t('batch.rasterModeIntensity')}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </>
  );

  if (status === 'WAITING') {
    return (
      <>
        <div className='flex flex-col gap-2 items-center justify-center'>
          <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.WAITING.icon}</div>
          <span className='text-sm text-slate-500 dark:text-stone-400 font-medium text-left'>
            {t('batch.readyForProcessing')}
          </span>
        </div>
        {processTypeCandidates.ambiguous ? (
          <ProcessTypeHint
            id={processTypeHintId}
            acknowledged={processTypeAcknowledged}
            className='mt-4 w-fit max-w-full'
          />
        ) : null}
        <div
          className={cn(
            'grid grid-cols-1 min-[400px]:grid-cols-2 gap-4 mb-4',
            processTypeCandidates.ambiguous ? 'mt-3' : 'mt-8',
          )}
        >
          {/* Process type comes first: it decides which of the other fields apply,
              and the ambiguity hint above points at this cell. */}
          <div className='flex items-start gap-1 flex-col'>
            <Label>{t('batch.processType')}</Label>
            <Select
              value={formik.values.processType}
              onOpenChange={(open) => {
                if (open) setProcessTypeAcknowledged(true);
              }}
              onValueChange={(value) => {
                setProcessTypeAcknowledged(true);
                if (isGatedGeotiffType(value)) {
                  geotiffGate.promptUpgrade();
                  return;
                }
                formik.setFieldValue('processType', value);
              }}
            >
              <SelectTrigger
                className={cn(
                  'w-full',
                  processTypeCandidates.ambiguous &&
                    !processTypeAcknowledged &&
                    'ring-2 ring-amber-400 ring-offset-1 ring-offset-background dark:ring-amber-500',
                )}
                aria-describedby={processTypeCandidates.ambiguous ? processTypeHintId : undefined}
              >
                <SelectValue placeholder={t('batch.selectProcessType')} />
              </SelectTrigger>
              <SelectContent>
                {processTypeGroups.map(({ category, types }) => (
                  <SelectGroup key={category}>
                    <SelectLabel className='font-semibold'>{t(`batch.processTypeCategories.${category}`)}</SelectLabel>
                    {types.map((key) => (
                      <SelectItem
                        key={key}
                        value={key}
                        disabled={!processTypeCandidates.types.includes(key as ProcessJobType)}
                        className={isGatedGeotiffType(key) ? 'opacity-60' : undefined}
                      >
                        {t(`batch.processTypes.${key}`)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!['KML', 'GPKG'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.srid')}</Label>
              <SridSelector
                className='w-full'
                value={formik.values.srid.toString()}
                onChange={(value) => formik.setFieldValue('srid', parseInt(value))}
              />
            </div>
          ) : null}
          {formik.values.processType === 'DWG' ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.outputVersion')}</Label>
              <Select value={formik.values.dxfOutputVersion} onValueChange={formik.handleChange('dxfOutputVersion')}>
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder={t('batch.selectOutputVersion')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.entries(dxfOutputVersions).map(([key, value]) => (
                      <SelectItem key={key} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {['GEOTIFF', 'JP2', 'JPG'].includes(formik.values.processType) ? geotiffOptionFields : null}
          {['LAS', 'E57', 'PTS', 'RCS'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.pointCloudAttributes')}</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='w-full justify-between'>
                    {formik.values.pointCloudAttributes.length > 0
                      ? t('batch.selected', { count: formik.values.pointCloudAttributes.length })
                      : t('batch.selectAttributes')}
                    <ChevronDownIcon className='h-4 w-4 opacity-50' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='w-56'>
                  {Object.entries(pointCloudAttributes).map(([key, value]) => (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={formik.values.pointCloudAttributes.includes(key)}
                      disabled={!['rgb', 'intensity'].includes(key) && formik.values.processType === 'E57'}
                      onSelect={(event) => {
                        event.preventDefault();
                      }}
                      onCheckedChange={(checked) => {
                        const currentAttributes = formik.values.pointCloudAttributes;
                        if (checked) {
                          formik.setFieldValue('pointCloudAttributes', [...currentAttributes, key]);
                        } else {
                          formik.setFieldValue(
                            'pointCloudAttributes',
                            currentAttributes.filter((attr) => attr !== key),
                          );
                        }
                      }}
                    >
                      {t(`batch.pcAttributes.${pcAttributeKeys[key]}`)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}
          {[
            'E57',
            'FJD_IMAGE',
            'TRIMBLE_IMAGE',
            'TOPCON_IMAGE',
            'SOUTH_IMAGE',
            'OMNISLAM_IMAGE',
            'LEICA_IMAGE',
            'GVI_IMAGE',
            'DJI_IMAGE',
            'CHC_IMAGE',
            'MOSAIC_IMAGE',
            'IPRJ',
          ].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.compressImages')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.compressImages}
                  onCheckedChange={(checked) => formik.setFieldValue('compressImages', checked)}
                />
              </div>
            </div>
          ) : null}
          {isPrivacyBlurEnabled &&
          [
            'E57',
            'FJD_IMAGE',
            'TRIMBLE_IMAGE',
            'TOPCON_IMAGE',
            'SOUTH_IMAGE',
            'OMNISLAM_IMAGE',
            'LEICA_IMAGE',
            'GVI_IMAGE',
            'DJI_IMAGE',
            'CHC_IMAGE',
            'MOSAIC_IMAGE',
          ].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.blurLicensePlates')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.blurLicensePlates}
                  onCheckedChange={(checked) => formik.setFieldValue('blurLicensePlates', checked)}
                />
              </div>
            </div>
          ) : null}
          {isPrivacyBlurEnabled &&
          [
            'E57',
            'FJD_IMAGE',
            'TRIMBLE_IMAGE',
            'TOPCON_IMAGE',
            'SOUTH_IMAGE',
            'OMNISLAM_IMAGE',
            'LEICA_IMAGE',
            'GVI_IMAGE',
            'DJI_IMAGE',
            'CHC_IMAGE',
            'MOSAIC_IMAGE',
          ].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label>{t('batch.blurFaces')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.blurFaces}
                  onCheckedChange={(checked) => formik.setFieldValue('blurFaces', checked)}
                />
              </div>
            </div>
          ) : null}
          {['LAS', 'PTS', 'E57'].includes(formik.values.processType) ? (
            <div className={`flex items-start gap-1 flex-col ${!rcsGate.allowed ? 'opacity-60' : ''}`}>
              <Label>{t('batch.generateRcs')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={rcsGate.allowed && formik.values.generateRcs}
                  onCheckedChange={(checked) => {
                    if (!rcsGate.allowed) {
                      rcsGate.promptUpgrade();
                      return;
                    }
                    formik.setFieldValue('generateRcs', checked);
                  }}
                />
              </div>
            </div>
          ) : null}
          {offersMap2d(formik.values.processType) ? (
            <div className={`flex items-start gap-1 flex-col ${!map2dGate.allowed ? 'opacity-60' : ''}`}>
              <Label>{t('batch.generate2dMap')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  data-testid='batch-generate2dMap'
                  indeterminate={false}
                  checked={map2dGate.allowed && formik.values.generate2dMap}
                  onCheckedChange={(checked) => {
                    if (!map2dGate.allowed) {
                      map2dGate.promptUpgrade();
                      return;
                    }
                    formik.setFieldValue('generate2dMap', checked);
                  }}
                />
              </div>
              {formik.values.processType === 'IFC_FRAGMENT' && (
                <p className='text-xs text-muted-foreground'>{t('batch.generate2dMapIfcHint')}</p>
              )}
            </div>
          ) : null}
          {['LAS', 'E57'].includes(formik.values.processType) ? (
            <div className='flex items-start gap-1 flex-col'>
              <Label title={t('batch.generateDecimatedHint')}>{t('batch.generateDecimated')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  checked={formik.values.generateDecimatedPointCloud}
                  onCheckedChange={(checked) => formik.setFieldValue('generateDecimatedPointCloud', checked)}
                />
              </div>
            </div>
          ) : null}
          {isPointCloudType ? (
            <div className={`flex items-start gap-1 flex-col ${!geotiffGate.allowed ? 'opacity-60' : ''}`}>
              <Label>{t('batch.generateGeotiff')}</Label>
              <div className='flex items-center flex-1'>
                <Checkbox
                  indeterminate={false}
                  disabled={!geotiffAllowed}
                  checked={generatesGeotiff}
                  onCheckedChange={(checked) => {
                    if (!geotiffGate.allowed) {
                      geotiffGate.promptUpgrade();
                      return;
                    }
                    formik.setFieldValue('generateGeotiff', checked);
                  }}
                />
              </div>
              {!geotiffAllowed ? (
                <span className='text-xs text-slate-500 dark:text-stone-400'>
                  {t('batch.generateGeotiffNeedsSrid')}
                </span>
              ) : null}
            </div>
          ) : null}
          {generatesGeotiff ? geotiffOptionFields : null}
          <div className='flex items-start gap-1 flex-col justify-end'>
            {/* <Label>Output Version</Label> */}
            <Button type='submit' className='w-full' onClick={formik.submitForm} disabled={formik.isSubmitting}>
              {t('batch.process')}
            </Button>
          </div>
        </div>
      </>
    );
  } else if (status === 'UPLOADING') {
    // Processing options (and the Process button) only unlock once the batch
    // is sealed as WAITING — say why instead of rendering nothing.
    return (
      <div className='flex flex-col gap-1 items-center justify-center mb-10' data-testid='batch-uploading-notice'>
        <div className='[&>svg]:size-10 flex items-center justify-center mb-1'>{batchStatusMap.UPLOADING.icon}</div>
        <span className='text-sm text-slate-700 dark:text-stone-200 font-semibold'>{t('batch.uploadingTitle')}</span>
        <span className='text-sm text-slate-500 dark:text-stone-400 font-medium text-center'>
          {t('batch.uploadingDescription')}
        </span>
      </div>
    );
  } else if (status === 'PROCESSING') {
    return (
      <div className='flex flex-col gap-2 items-center justify-center mb-10'>
        <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.PROCESSING.icon}</div>
        <span className='text-sm text-slate-500 font-medium text-left'>{t('batch.processing')}</span>
      </div>
    );
  } else if (status === 'FAILED') {
    return (
      <div className='flex flex-col gap-2 items-center justify-center mb-4'>
        <div className='[&>svg]:size-10 flex items-center justify-center'>{batchStatusMap.FAILED.icon}</div>
        <span className='text-sm text-slate-500 font-medium text-left'>{t('batch.failedToProcess')}</span>
      </div>
    );
  }
  return null;
};

export default Header;
